import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const LocationContext = createContext(null);

const DEFAULT_LOCATION = { lat: 19.03908, lng: -98.33858 };

/* Limpieza única: una versión de pruebas de diseño sembraba a la fuerza la
 * dirección "Ayuntamiento 190" (San Luis Tehuiloyocan) en cada apertura, y
 * llegó a producción (vachido.com, 2026-10-15). Quien la abrió esos días la
 * tiene guardada como si fuera suya: se borra para que se le pida su
 * ubicación real. Se puede quitar cuando ya no quede nadie con ella. */
try {
  if (localStorage.getItem('userAddress') === 'Ayuntamiento 190'
    && localStorage.getItem('userCoordinates') === JSON.stringify({ lat: 19.03908, lng: -98.33858 })) {
    ['userAddress', 'userAddressDetail', 'userCoordinates'].forEach((k) => localStorage.removeItem(k));
  }
} catch { /* sin storage: nada que limpiar */ }

// El modal se abre al entrar solo si no hay una dirección guardada. Con una ya
// guardada no se vuelve a pedir; se cambia desde el header.
const shouldOpenOnMount = () => {
  try {
    return !localStorage.getItem('userAddress');
  } catch {
    return true;
  }
};

/**
 * Fuente única de verdad para:
 *  - Ubicación geográfica actual (`location`, `loading`, `permissionStatus`)
 *  - Estado del modal de selección de dirección (`isLocationModalOpen`,
 *    `openLocationModal`, `closeLocationModal`)
 *
 * El modal arranca abierto si no hay una dirección guardada; con una guardada,
 * no se abre solo. Se cierra cuando se selecciona una dirección — la X y el
 * cierre por backdrop están removidos del sheet.
 */
export function LocationProvider({ children }) {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionStatus, setPermissionStatus] = useState(null);
  const [isLocationModalOpen, setLocationModalOpen] = useState(shouldOpenOnMount);

  const openLocationModal = useCallback(() => setLocationModalOpen(true), []);
  const closeLocationModal = useCallback(() => setLocationModalOpen(false), []);

  // Inicialización al montar: usamos coordenadas guardadas o DEFAULT_LOCATION.
  //
  // IMPORTANTE: NO pedimos navigator.geolocation acá. Disparar el prompt del
  // browser al cargar la página es anti-pattern (Lighthouse Best Practices lo
  // marca, y el usuario rechaza ~70% porque no entiende por qué se le pide).
  //
  // La geolocation se solicita on-demand desde LocationModal cuando el usuario
  // tap "Usar mi ubicación" → llama a requestLocation() de este context.
  useEffect(() => {
    const savedCoords = localStorage.getItem('userCoordinates');
    if (savedCoords) {
      try {
        const coords = JSON.parse(savedCoords);
        const lat = coords.lat || coords[1];
        const lng = coords.lng || coords[0];
        if (lat && lng) {
          setLocation({ lat, lng });
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }
    }
    setLocation(DEFAULT_LOCATION);
    setLoading(false);
  }, []);

  // Escuchar cambios de permisos
  useEffect(() => {
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        setPermissionStatus(result.state);
        result.onchange = () => setPermissionStatus(result.state);
      });
    }
  }, []);

  // Escuchar evento locationUpdated del LocationModal
  useEffect(() => {
    const handler = (e) => {
      const { coordinates } = e.detail;
      if (coordinates) {
        const lat = coordinates.lat || coordinates[1];
        const lng = coordinates.lng || coordinates[0];
        setLocation({ lat, lng });
      }
    };
    window.addEventListener('locationUpdated', handler);
    return () => window.removeEventListener('locationUpdated', handler);
  }, []);

  // Función para solicitar geolocalización manualmente
  const requestLocation = useCallback(() => {
    if (navigator.geolocation) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy
          };
          setLocation(newLoc);
          localStorage.setItem('userCoordinates', JSON.stringify(newLoc));
          setLoading(false);
        },
        () => setLoading(false),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
  }, []);

  const contextValue = useMemo(() => ({
    location,
    loading,
    permissionStatus,
    requestLocation,
    isLocationModalOpen,
    openLocationModal,
    closeLocationModal,
  }), [location, loading, permissionStatus, requestLocation, isLocationModalOpen, openLocationModal, closeLocationModal]);

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
}

export const useUserLocation = () => {
  const context = useContext(LocationContext);
  if (!context) throw new Error('useUserLocation must be used within LocationProvider');
  return context;
};
